import { ActionType } from '@editor/src/js/constants/common-action-type';
import { IMenuEventParams } from '@src/slide/application/service/menu-manager/menu-event/menu-event.interface';
import { IRowColStartEnd, ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import * as shapeEvent from '@src/slide/application/service/menu-manager/menu-event/model/shape';
import { ITextEditorStateService } from '@src/slide/application/service/text-editor-state-service/text-editor-state-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IInsertTableRowColManager } from '@src/slide/application/service/insert-table-row-col-manager/index.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { transaction } from '@src/slide/domain/slide-app';

/**
 * 事件列表（主要逻辑从model文件迁移而来）
 */
const menuEventMap = {
    [ActionType.INSERT_ROW_TOP]: (model: ITableModel) => {
        model.handleInsertRowTop(getInsertRange(ActionType.INSERT_ROW_TOP));
    },
    [ActionType.INSERT_ROW_BOTTOM]: (model: ITableModel) => {
        model.handleInsertRowBottom(getInsertRange(ActionType.INSERT_ROW_BOTTOM));
    },
    [ActionType.INSERT_COL_LEFT]: (model: ITableModel) => {
        model.handleInsertColLeft(getInsertRange(ActionType.INSERT_COL_LEFT));
    },
    [ActionType.INSERT_COL_RIGHT]: (model: ITableModel) => {
        model.handleInsertColRight(getInsertRange(ActionType.INSERT_COL_RIGHT));
    },
    [ActionType.DELETE_ROW]: (model: ITableModel) => model.handleDeleteRow(),
    [ActionType.DELETE_COL]: (model: ITableModel) => model.handleDeleteCol(),
    [ActionType.DELETE_TABLE]: (model: ITableModel) => model.handleDeleteTable(),
    [ActionType.MERGE_CELL]: (model: ITableModel) => model.handleMergeCell(),
};

const getAllTableCell = (model: ITableModel): IRowColStartEnd[] => {
    const rowStart = 0;
    const colStart = 0;
    const rowEnd = model.props.rowHeights.length - 1;
    const colEnd = model.props.colWidths.length - 1;
    const range = {
        rowStart: Math.min(rowStart, rowEnd),
        colStart: Math.min(colStart, colEnd),
        rowEnd: Math.max(rowStart, rowEnd),
        colEnd: Math.max(colStart, colEnd),
    };

    const selectedRange = model.calculatSelectedRange(range);
    const selectedCells = model.calculatSelectedCells(selectedRange);
    return selectedCells;
};

/**
 * undo redo单独处理，因为单元格是否处于编辑状态决定是否继续走后续的外层通用undo redo
 * @param model
 * @param menuEventParams
 */
const handleUndoRedo = (model: ITableModel, menuEventParams: IMenuEventParams): boolean => {
    if (model.selectedCellRanges.length !== 1) {
        return false;
    }
    const [{ rowStart, colStart }] = model.selectedCellRanges;
    return shapeEvent.handleMenuEvent(model.props.items[rowStart][colStart], menuEventParams);
};

/**
 * model迁移而来
 * @param model
 * @param menuEventParams
 */
const handleCellText = (model: ITableModel, menuEventParams: IMenuEventParams) => {
    let handleResult = false;
    if (model.selectedCellRanges !== null) {
        let isEditing = false;
        if (model.selectedCellRanges.length === 1) {
            const [{ rowStart, colStart }] = model.selectedCellRanges;
            const shape = model.props.items[rowStart][colStart];
            isEditing = shape?.editorInstance?.isEditing;
            if (!isEditing && shape.isEditing) {
                model.uploadEditingCellData(true);
            }
        }

        // 编辑中的单元格走异步修改逻辑，由editor触发上传逻辑
        // 非编辑中的单元格走同步修改逻辑，由表格统一处理上传
        const newMenuEventParams = {
            ...menuEventParams,
            extraOption: {
                ...menuEventParams.extraOption,
                isMultiGroup: !isEditing,
                needUpdate: isEditing,
            },
        };
        transaction(() => {
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < model.selectedCellRanges.length; i++) {
                const cellRange = model.selectedCellRanges[i];
                const shapeModel = model.props.items[cellRange.rowStart][cellRange.colStart];
                shapeModel.parent = model.parent;
                const result = shapeEvent.handleMenuEvent(shapeModel, newMenuEventParams);
                handleResult = handleResult || result;
            }
            // 正在编辑或者文字效果预览的单元格这里不处理数据上传
            if (!isEditing && !getTextEditorStateService().isTextPreview && handleResult) {
                model.uploadSelectedCellData();
            }
        });
    }
    return handleResult;
};

/**
 * model迁移而来
 * @param model
 * @param menuEventParams
 */
const handleFontWithoutEditor = (model: ITableModel, menuEventParams: IMenuEventParams) => {
    let handleResult = false;
    const newMenuEventParams = {
        ...menuEventParams,
        extraOption: {
            ...menuEventParams.extraOption,
            isMultiGroup: true,
            needUpdate: false,
        },
    };
    const allCellRange = getAllTableCell(model);
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < allCellRange.length; i++) {
        const cellRange = allCellRange[i];
        const shapeModel = model.props.items[cellRange.rowStart][cellRange.colStart];
        shapeModel.parent = model.parent;
        const result = shapeEvent.handleMenuEvent(shapeModel, newMenuEventParams);
        handleResult = handleResult || result;
    }
    // 正在编辑或者文字效果预览的单元格这里不处理数据上传
    if (handleResult) {
        model.uploadAllCellData();
    }
    return handleResult;
};

/**
 * 事件响应匹配
 * 返回boolean值，表示此事件是否已在model独有事件匹配上，匹配上返回TRUE，结束匹配。未匹配上返回FALSE，外层继续匹配通用事件
 * @param model 当前uiModel
 * @param menuEventParams 菜单传参
 */
export function handleMenuEvent(model: ITableModel, menuEventParams: IMenuEventParams): boolean {
    const { actionType } = menuEventParams;
    // 统一字体，不走带editor的逻辑
    if (actionType === ActionType.UNIFORM_FONT) {
        return handleFontWithoutEditor(model, menuEventParams);
    }
    // 未选中
    if (!model.selectedRange) {
        return false;
    }
    // undo redo单独处理，因为单元格是否处于编辑状态决定是否继续走后续的外层通用undo redo
    if ([ActionType.UNDO, ActionType.REDO].includes(actionType)) {
        return handleUndoRedo(model, menuEventParams);
    }
    // 精准查找事件
    if (menuEventMap[actionType]) {
        menuEventMap[actionType](model);
        return true;
    }
    return handleCellText(model, menuEventParams);
}

function getTextEditorStateService(): ITextEditorStateService {
    return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
        .viewableServiceLayer.textEditorStateService;
}

function getInsertTableManager(): IInsertTableRowColManager | undefined {
    return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
        .editableServiceLayer?.insertTableRowColManager;
}

function getInsertRange(actionType) {
    const insertTableRowColManager = getInsertTableManager();
    let rowSpan = 1; let colSpan = 1;
    switch (actionType) {
        case ActionType.INSERT_ROW_TOP:
            rowSpan = insertTableRowColManager?.insertTopRowNumber || 1;
            break;
        case ActionType.INSERT_ROW_BOTTOM:
            rowSpan = insertTableRowColManager?.insertBottomRowNumber || 1;
            break;
        case ActionType.INSERT_COL_LEFT:
            colSpan = insertTableRowColManager?.insertLeftColNumber || 1;
            break;
        case ActionType.INSERT_COL_RIGHT:
            colSpan = insertTableRowColManager?.insertRightColNumber || 1;
            break;
    }

    return {
        rowSpan,
        colSpan,
    };
}
